'use client'

import { useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import type { Labels } from 'types/labels'
import { env } from 'utils/envManager'

import type { LabelsService } from '../../types'

interface UseClientLabelsProps {
	countryISO: string
	languageISO: string
	keys?: string[]
	suspense?: boolean
}

export function useClientLabels({
	keys,
	countryISO,
	languageISO,
	suspense = true,
}: UseClientLabelsProps): {
	labels: Labels | undefined
	t: (key: string) => string
} {
	const doFetch = (keys ?? []).length !== 0
	const url = new URL(
		`${env.NEXT_PUBLIC_SITE_BASE_URL}/ws-labels/labels/v2/${(keys ?? []).join(',')}`
	)
	url.searchParams.append('languageIso', languageISO)
	url.searchParams.append('countryIso', countryISO.toUpperCase())

	const { data } = useSWRImmutable<LabelsService | null>(
		[keys],
		() =>
			doFetch
				? fetch(url.href, {
						next: { revalidate: 0 },
					}).then((res) => res.json())
				: null,
		{ suspense }
	)

	return useMemo(
		() => ({
			labels: data?.translations,
			t: (key: string) => data?.translations?.[key] || key,
		}),
		[data]
	)
}
